import { useEffect, useRef, useState } from "react";
import Logo, { invalidateBrandingCache } from "../../components/common/Logo";

const BASE = "https://support.gs-group.fr/api";
const MAX_SIZE = 5 * 1024 * 1024;
const ACCEPTED = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];

function authHeaders() {
  return { Authorization: "Bearer " + localStorage.getItem("nexo_token") };
}

// Aperçu d'un conteneur réel de l'appli — utilise le composant <Logo/> lui-même (pas une
// resimulation) pour garantir que ce qu'on voit ici est exactement ce qui s'affiche
// ailleurs dans le CRM, y compris la plaque de contraste sur fonds sombres.
function PreviewBox({ label, bg, height = 96, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        height, borderRadius: 12, background: bg, border: "1px solid rgba(0,0,0,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 12, overflow: "hidden",
      }}>
        {children}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

const NAVY = "linear-gradient(160deg,#1a3d6e 0%,#071020 65%)";

export default function BrandingSettings({ T }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/settings`, { headers: authHeaders() });
      const data = await res.json();
      setSettings(data);
      setCompanyName(data.company_name || "GS GROUP");
    } catch (e) { setError("Impossible de charger les réglages"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const flash = (msg) => { setOk(msg); setTimeout(() => setOk(""), 3000); };

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return setError("Format non supporté — utilisez un PNG, JPG ou SVG.");
    if (file.size > MAX_SIZE) return setError("Fichier trop volumineux (5 Mo maximum).");

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const res = await fetch(`${BASE}/settings/logo`, { method: "POST", headers: authHeaders(), body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Échec de l'envoi");
      setSettings(data);
      invalidateBrandingCache();
      flash("✅ Logo mis à jour !");
    } catch (e) {
      setError(e.message || "Erreur réseau lors de l'envoi du logo");
    }
    setUploading(false);
  };

  const handleRemove = async () => {
    if (!window.confirm("Revenir au logo par défaut GS Group ?")) return;
    setUploading(true);
    try {
      const res = await fetch(`${BASE}/settings/logo`, { method: "DELETE", headers: authHeaders() });
      const data = await res.json();
      setSettings(data);
      invalidateBrandingCache();
      flash("Logo réinitialisé.");
    } catch (e) { setError("Erreur lors de la suppression"); }
    setUploading(false);
  };

  const handleSaveName = async () => {
    if (!companyName.trim()) return;
    setSavingName(true);
    try {
      const res = await fetch(`${BASE}/settings`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ company_name: companyName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSettings(data);
      invalidateBrandingCache();
      flash("✅ Nom enregistré !");
    } catch (e) { setError("Erreur lors de l'enregistrement du nom"); }
    setSavingName(false);
  };

  const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 15, padding: 24 };

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={card}>
          <div style={{ height: 16, width: 220, borderRadius: 6, background: T.border, marginBottom: 14, animation: "pulse 1.4s ease-in-out infinite" }}/>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ height: 96, borderRadius: 12, background: T.bg, animation: "pulse 1.4s ease-in-out infinite" }}/>
            ))}
          </div>
          <style>{"@keyframes pulse{0%,100%{opacity:.5}50%{opacity:1}}"}</style>
        </div>
      </div>
    );
  }

  const logoUrl = settings?.logo_url;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>🖼️ Logo & Identité visuelle</div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
          Le logo est automatiquement rogné (suppression des marges blanches), redimensionné en conservant son ratio,
          et décliné en icône carrée pour les emplacements compacts (favicon, sidebar réduite).
        </div>

        {error && <div style={{ background: "#ef444418", border: "1px solid #ef444440", color: "#ef4444", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>⚠️ {error}</div>}
        {ok && <div style={{ background: "#10b98118", border: "1px solid #10b98140", color: "#10b981", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{ok}</div>}

        {/* Zone d'upload */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? T.accent : T.border}`, borderRadius: 14, padding: 28,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
            cursor: uploading ? "not-allowed" : "pointer", background: dragOver ? `${T.accent}0a` : T.bg,
            transition: "all 0.15s", marginBottom: 22,
          }}
        >
          <input ref={fileInputRef} type="file" accept={ACCEPTED.join(",")} style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}/>
          {uploading ? (
            <><span style={{ fontSize: 28, animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</span>
              <div style={{ fontSize: 13, color: T.muted }}>Traitement de l'image…</div></>
          ) : (
            <><span style={{ fontSize: 28 }}>📤</span>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Cliquez ou glissez votre logo ici</div>
              <div style={{ fontSize: 11, color: T.muted }}>PNG, JPG ou SVG · 5 Mo maximum</div></>
          )}
          <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
        </div>

        {/* Aperçu multi-contextes */}
        <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
          Prévisualisation en direct
        </div>
        <div key={settings?.updated_at || "default"} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 20 }}>
          <PreviewBox label="Sidebar réduite (fond sombre)" bg={NAVY}>
            <Logo variant="sidebar-dark" collapsed size={20}/>
          </PreviewBox>
          <PreviewBox label="Sidebar dépliée (fond sombre)" bg={NAVY}>
            <Logo variant="sidebar-dark" size={22}/>
          </PreviewBox>
          <PreviewBox label="Header (fond clair)" bg="#ffffff">
            <Logo variant="header-light" size={30}/>
          </PreviewBox>
          <PreviewBox label="Document PDF" bg="#ffffff">
            {logoUrl
              ? <img src={logoUrl} alt="" style={{ maxHeight: 48, maxWidth: "85%", objectFit: "contain" }}/>
              : <span style={{ color: "#0f172a", fontSize: 12, fontWeight: 700, opacity: 0.5 }}>{companyName || "GS GROUP"}</span>}
          </PreviewBox>
          <PreviewBox label="Écran de connexion" bg={NAVY}>
            <Logo variant="login" size={32}/>
          </PreviewBox>
        </div>

        {logoUrl && (
          <button onClick={handleRemove} disabled={uploading}
            style={{ padding: "9px 16px", borderRadius: 10, border: `1px solid #ef444440`, background: "transparent", color: "#ef4444", fontSize: 12, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer" }}>
            🗑️ Réinitialiser le logo par défaut
          </button>
        )}
      </div>

      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 14 }}>🏢 Nom de la société</div>
        <div style={{ display: "flex", gap: 10 }}>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="GS GROUP"
            style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: "none" }}/>
          <button onClick={handleSaveName} disabled={savingName || !companyName.trim()}
            style={{ padding: "11px 20px", borderRadius: 10, border: "none", background: savingName ? "#6b7280" : T.accent, color: "#fff", fontSize: 13, fontWeight: 700, cursor: savingName ? "not-allowed" : "pointer" }}>
            {savingName ? "…" : "Enregistrer"}
          </button>
        </div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>Utilisé dans l'en-tête des documents PDF et des emails à défaut de logo personnalisé.</div>
      </div>
    </div>
  );
}
